<template>
  <div id="multi-special-upload" class="align-start fill-height">
    <v-row class="g-container-large" no-gutters>
      <v-col cols="12">
        <h2 class="ma-0">{{ $route.meta.title }}</h2>

        <template v-if="$vuetifyDisplay.smAndDown">
          <v-card class="mt-4 px-8 py-6">
            <div class="text-center">
              To use this feature you must access your Supplier portal from a
              Desktop device
            </div>
            <div class="text-center">
              <v-btn
                class="i-btn-block-secondary mt-4"
                rounded
                :to="{ name: 'dashboard' }"
                >Dashboard</v-btn
              >
            </div>
          </v-card>
        </template>

        <template v-else>
          <v-row>
            <v-spacer></v-spacer>
            <v-col cols="12" md="3" lg="2">
              <i-instructions
                instructionKey="data-maintenance/specials/multi-upload"
              ></i-instructions>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <div>
                <i-multi-special-upload-row
                  v-for="(special, index) in specials"
                  :key="index"
                  :is-duplicate="hasDuplicate(index)"
                  :family-groups="enrichedFamilyGroups"
                  :products="productOptionsForRow(index)"
                  :products-loading="rowProductsLoading(index)"
                  :special="special"
                  :specialPosition="index"
                  :specialsQty="specials.length"
                  :is-only-row="specials.length === 1"
                  :is-row-valid="isRowValid(special, index)"
                  :initial-value="initialRowValue"
                  @change="onFieldChange(index, $event)"
                  @remove="onSpecialRemove(index)"
                  @add="onSpecialAdd(index)"
                  @duplicate="onDuplicate(index)"
                  @clear="onClear(index)"
                  @ensure-products="ensureProductsLoaded(index)"
                  @search-products="onProductSearch(index, $event)"
                />
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" class="upload-specials-col text-right">
              <v-card class="pa-4">
                <v-btn
                  class="i-btn-block-secondary"
                  :disabled="!isFormValid"
                  :loading="saving"
                  @click="onSave"
                >
                  <strong>Upload Specials</strong>
                </v-btn>
              </v-card>
            </v-col>
          </v-row>
        </template>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions, mapState } from 'vuex'
import { createMultiSpecials } from '@/services/promotions'
import { MultiSpecialUploadHelper } from '@/modules/dataMaintenance/helper/MultiSpecialUploadHelper'
import SpecialsHelper from '@/modules/dataMaintenance/helper/SpecialsHelper'
import LocalStorageHelper from '@/components/helpers/LocalStorageHelper'
import iMultiSpecialUploadRow from '../components/MultiSpecialUploadRow'
import iInstructions from '@/components/Instructions'
import moment from 'moment-timezone'
import { SPECIAL_TYPE } from '@/modules/dataMaintenance/constants/Special.constants'
import {
  buildUpcomingPriceChangeProductCodeSet,
  familyGroupHasUpcomingPriceChange,
  productHasUpcomingPriceChange,
  withUpcomingPriceChangeDisabled
} from '@/modules/dataMaintenance/helper/PromotionProductAvailabilityHelper'

const MULTI_SPECIALS_STORAGE_KEY_PREFIX = 'multiple-specials'
const initialSpecial = {
  regionalPriceMap: []
}
const PRODUCT_SEARCH_RESULT_LIMIT = 25

export default {
  name: 'multi-special-upload',
  components: {
    iMultiSpecialUploadRow,
    iInstructions
  },

  data: () => ({
    saving: false,
    specials: [],
    initialRowValue: initialSpecial,
    productOptionsByRow: {},
    productsLoadingByRow: {},
    hasLoadedProductsByRow: {}
  }),

  computed: {
    ...mapState('preferences', ['selectedSupplierId']),
    ...mapState('familyGroups', ['familyGroups']),
    ...mapState('products', {
      products: 'products',
      productsLoading: 'loading'
    }),
    ...mapState('pricingRegions', ['pricingRegions']),

    localStorageKey() {
      return `${MULTI_SPECIALS_STORAGE_KEY_PREFIX}-${this.selectedSupplierId}`
    },

    isFormValid() {
      return MultiSpecialUploadHelper.validateSpecials(this.specials)
    },

    upcomingPriceChangeProductCodes() {
      return buildUpcomingPriceChangeProductCodeSet(
        this.products,
        this.familyGroups
      )
    },

    enrichedFamilyGroups() {
      if (!this.familyGroups) {
        return []
      }
      return (
        this.familyGroups
          // filter out family groups with no products
          .filter((f) => f.productCodes.length)
          .map((f) => ({ text: f.name, value: f }))
          .map((option) =>
            withUpcomingPriceChangeDisabled(
              option,
              familyGroupHasUpcomingPriceChange(
                option.value,
                this.upcomingPriceChangeProductCodes
              )
            )
          )
          .sort((f1, f2) => (f1.text < f2.text ? -1 : 1))
      )
    },

    isProductsLoading() {
      return this.productsLoading
    }
  },

  created() {
    this.productSearchDebouncersByRow = {}
    this.productDetailsByCode = {}
  },

  methods: {
    ...mapActions('products', ['fetchProducts']),
    ...mapActions('notifications', ['addNotification']),
    ...mapActions('familyGroups', ['fetchFamilyGroups']),

    normalizeProductsResponse(response) {
      if (Array.isArray(response)) return response
      return response?.products || []
    },

    cacheProducts(products = []) {
      products.forEach((product) => {
        if (!product?.productCode) return
        this.productDetailsByCode[product.productCode] = product
      })
    },

    productOptionsForRow(index) {
      return this.productOptionsByRow[index] || []
    },

    rowProductsLoading(index) {
      return !!this.productsLoadingByRow[index]
    },

    createProductOption(product) {
      return withUpcomingPriceChangeDisabled(
        {
          text: this.productDisplayName(product),
          value: product
        },
        productHasUpcomingPriceChange(
          product,
          this.familyGroups,
          this.upcomingPriceChangeProductCodes
        )
      )
    },

    pinSelectedProductOption(index, selectedProduct) {
      if (!selectedProduct?.productCode) return

      const selectedOption = this.createProductOption(selectedProduct)
      const currentOptions = this.productOptionsForRow(index)
      const nextOptions = [
        selectedOption,
        ...currentOptions.filter(
          (option) => option?.value?.productCode !== selectedProduct.productCode
        )
      ]

      this.productOptionsByRow[index] = nextOptions
    },

    setRowProductOptions(index, products = [], selectedProduct = null) {
      const options = products
        .filter((product) => product.status === 'LIVE')
        .map((product) => this.createProductOption(product))
        .sort((left, right) => (left.text < right.text ? -1 : 1))

      const pinnedProduct = selectedProduct?.productCode
        ? selectedProduct
        : this.specials[index]?.selectedProduct

      if (pinnedProduct?.productCode) {
        const selectedOption = this.createProductOption(pinnedProduct)
        this.productOptionsByRow[index] = [
          selectedOption,
          ...options.filter(
            (option) =>
              option?.value?.productCode !== selectedOption.value.productCode
          )
        ]
        return
      }

      this.productOptionsByRow[index] = options
    },

    async fetchProductsForRow(index, query = '') {
      this.productsLoadingByRow[index] = true

      try {
        const response = await this.fetchProducts({
          supplierId: this.selectedSupplierId,
          status: 'LIVE',
          page: 0,
          size: PRODUCT_SEARCH_RESULT_LIMIT,
          query: query || undefined
        })
        const products = this.normalizeProductsResponse(response)
        this.cacheProducts(products)
        this.setRowProductOptions(index, products)
        this.hasLoadedProductsByRow[index] = true
      } finally {
        this.productsLoadingByRow[index] = false
      }
    },

    ensureProductsLoaded(index) {
      if (this.hasLoadedProductsByRow[index]) return Promise.resolve()
      return this.fetchProductsForRow(index)
    },

    onProductSearch(index, query = '') {
      const normalizedQuery = query.trim()

      if (!normalizedQuery) {
        return this.fetchProductsForRow(index)
      }

      if (!this.productSearchDebouncersByRow[index]) {
        this.productSearchDebouncersByRow[index] = _.debounce(
          (nextQuery) => this.fetchProductsForRow(index, nextQuery),
          250
        )
      }

      return this.productSearchDebouncersByRow[index](normalizedQuery)
    },

    isRowValid(special, index) {
      return (
        MultiSpecialUploadHelper.isSpecialDataValid(special) &&
        this.isPromotionProductAvailable(special) &&
        MultiSpecialUploadHelper.isSpecialPricingValid(special) &&
        !MultiSpecialUploadHelper.isRowDuplicate(this.specials, special, index)
      )
    },

    isPromotionProductAvailable(special) {
      return (
        !productHasUpcomingPriceChange(
          special?.selectedProduct,
          this.familyGroups,
          this.upcomingPriceChangeProductCodes
        ) &&
        !familyGroupHasUpcomingPriceChange(
          special?.selectedFamilyGroup,
          this.upcomingPriceChangeProductCodes
        )
      )
    },

    hasDuplicate(specialIndex) {
      return MultiSpecialUploadHelper.isRowDuplicate(
        this.specials,
        this.specials[specialIndex],
        specialIndex
      )
    },

    async saveSpecials() {
      const specials = this.specials
      const processSpecialPayload = specials.map((special) => ({
        isServiceProduct: special.selectedProduct?.productType === 'SERVICE',
        familyGroupId: special.selectedFamilyGroup?.id,
        productCode: special.selectedProduct?.productCode,
        endDate: special.endDate,
        startDate: special.startDate,
        targetBannerGroupIds: special.targetBannerGroupIds,
        targetMultiStoreOperatorIds: special.targetMultiStoreOperatorIds,
        targetStoreIds: special.targetStoreIds,
        // Filter out zones without pricing
        zonePrices: special.regionalPriceMap
          .filter(
            (zone) =>
              zone.amountOff > 0 &&
              (special.selectedProduct?.productType === 'SERVICE' ||
                zone.unitRrp > 0)
          )
          .map((zone) => ({
            pricingRegionCode: zone.code,
            amountOff: zone.amountOff,
            unitRrp:
              zone.unitRrp > 0 ? zone.unitRrp : zone.revisedUnitPriceIncTax
          }))
      }))

      return await createMultiSpecials(
        this.selectedSupplierId,
        processSpecialPayload.map(({ isServiceProduct, ...payload }) => payload)
      )
    },

    async onSave() {
      this.saving = true
      try {
        await this.saveSpecials()
        await this.addNotification({
          color: 'secondary',
          text: 'Special(s) have been successfully created!'
        })
        LocalStorageHelper.removeItem(this.localStorageKey)
        // Prepare to allow user to create new Specials
        this.specials = [{ ...initialSpecial }]
      } catch (err) {
        let msg = null

        switch (err.response?.data?.code || 'UNKNOWN_ERROR') {
          case 'PROMOTION_OVERLAP':
            msg = 'There is an active Special for this product on this date...'
            break
          case 'VOLUME_PROMOTION_OVERLAP':
            msg =
              'There is an active Volume Buy for this product on this date...'
            break
          default:
            msg = 'Special could not be created, please try again...'
        }

        await this.addNotification({ color: 'error', text: msg })
      } finally {
        this.saving = false
        await this.$router.push({
          name: 'specials',
          query: { status: 'UPCOMING' }
        })
      }
    },

    productDisplayName(p) {
      return `${p.supplierItemId} / ${p.tradeItemDescription}`
    },

    enrichWithOriginalProductRrp(regionalPriceMap, referenceProduct) {
      return regionalPriceMap.map((zone) => ({
        ...zone,
        originalUnitRrp:
          zone.originalUnitRrp ||
          referenceProduct?.regionalPriceMap?.[zone.code]?.[0]?.regionUnitRrp ||
          referenceProduct?.regionalPriceMap?.[zone.code]?.[0]?.rrp ||
          null
      }))
    },

    getProductRegionalPriceMap(selectedProduct) {
      const regionalPriceMap =
        SpecialsHelper.transformProductToRegionalPriceMap(
          selectedProduct,
          this.pricingRegions,
          this.tenantCountryRegion
        ).sort((a, b) => (a.code > b.code ? 1 : -1))

      return this.enrichWithOriginalProductRrp(
        regionalPriceMap,
        selectedProduct
      )
    },

    getFamilyGroupRegionalPriceMap(selectedFamilyGroup) {
      let regionalPriceMap =
        SpecialsHelper.transformFamilyGroupZonePricesToRegionalPriceMap(
          selectedFamilyGroup,
          this.pricingRegions,
          this.tenantCountryRegion
        ).sort((a, b) => (a.code > b.code ? 1 : -1))

      regionalPriceMap = regionalPriceMap.map((zone) => {
        const sourcePrice = selectedFamilyGroup.zonePrices?.find(
          (price) => price.pricingRegionCode === zone.code
        )
        return {
          ...zone,
          originalUnitRrp:
            sourcePrice?.regionUnitRrp || sourcePrice?.rrp || null
        }
      })

      if (!regionalPriceMap.length) {
        // Fall back to product-derived pricing when zonePrices are unavailable.
        const familyProducts = Object.values(this.productDetailsByCode)
          .filter((product) =>
            (selectedFamilyGroup?.productCodes || []).includes(
              product.productCode
            )
          )
          .map((product) => ({
            text: this.productDisplayName(product),
            value: product
          }))

        const derivedRegionalPriceMap =
          SpecialsHelper.transformProductsToRegionalPriceMap(
            familyProducts.map((p) => p.value),
            this.pricingRegions,
            this.tenantCountryRegion
          ).sort((a, b) => (a.code > b.code ? 1 : -1))

        const familyProductValues = familyProducts.map((p) => p.value)
        const lowestPackageQuantityProduct = familyProductValues.find(
          (product) =>
            product.packageQuantity ===
            Math.min(...familyProductValues.map((p) => p.packageQuantity))
        )

        return this.enrichWithOriginalProductRrp(
          derivedRegionalPriceMap,
          lowestPackageQuantityProduct
        )
      }

      return regionalPriceMap
    },

    rehydrateSpecial(special) {
      if (!special) return special

      if (special.selectedFamilyGroup) {
        const selectedFamilyGroup =
          this.familyGroups.find(
            (familyGroup) => familyGroup.id === special.selectedFamilyGroup.id
          ) || special.selectedFamilyGroup

        return {
          ...special,
          selectedFamilyGroup,
          regionalPriceMap:
            this.getFamilyGroupRegionalPriceMap(selectedFamilyGroup)
        }
      }

      if (special.selectedProduct) {
        return {
          ...special,
          regionalPriceMap: this.getProductRegionalPriceMap(
            special.selectedProduct
          )
        }
      }

      return special
    },

    async onFieldChange(index, fields) {
      if (fields.specialTypes === SPECIAL_TYPE.BY_PRODUCT) {
        await this.ensureProductsLoaded(index)
      }

      if (fields.selectedFamilyGroup) {
        const regionalPriceMap = this.getFamilyGroupRegionalPriceMap(
          fields.selectedFamilyGroup
        )
        this.specials[index].regionalPriceMap = regionalPriceMap
      } else if (fields.selectedProduct) {
        this.cacheProducts([fields.selectedProduct])
        this.pinSelectedProductOption(index, fields.selectedProduct)
        const regionalPriceMap = this.getProductRegionalPriceMap(
          fields.selectedProduct
        )
        this.specials[index].regionalPriceMap = regionalPriceMap
      }
      for (const [key, value] of Object.entries(fields)) {
        this.specials[index][key] = value
      }
      MultiSpecialUploadHelper.validateSpecials(this.specials)
      this.triggerChanged()
    },

    onSpecialRemove(index) {
      this.specials.splice(index, 1)
      this.triggerChanged()
    },

    onSpecialAdd(index) {
      this.specials.splice(index + 1, 0, { ...initialSpecial })
      this.triggerChanged()
    },

    onDuplicate(index) {
      const duplicateIndex = index + 1
      const duplicate = _.cloneDeep(this.specials[index])
      this.copyProductRowState(index, duplicateIndex)
      this.specials.splice(duplicateIndex, 0, duplicate)
      this.pinSelectedProductOption(duplicateIndex, duplicate.selectedProduct)
      this.triggerChanged()
    },

    copyProductRowState(sourceIndex, duplicateIndex) {
      ;[
        this.productOptionsByRow,
        this.productsLoadingByRow,
        this.hasLoadedProductsByRow
      ].forEach((rowState) => {
        Object.keys(rowState)
          .map(Number)
          .filter((rowIndex) => rowIndex >= duplicateIndex)
          .sort((left, right) => right - left)
          .forEach((rowIndex) => {
            rowState[rowIndex + 1] = _.cloneDeep(rowState[rowIndex])
          })
        rowState[duplicateIndex] = _.cloneDeep(rowState[sourceIndex])
      })
    },

    triggerChanged() {
      LocalStorageHelper.setItem(this.localStorageKey, this.specials)
    },

    onClear(index) {
      this.specials[index] = { ...initialSpecial }
    }
  },

  async mounted() {
    try {
      // If local storage has specials that were going to start on a date in the past then it's likely they're
      // no longer relevant so, we filter them out here
      const futureLocalStorageSpecials =
        LocalStorageHelper.getItem(this.localStorageKey)?.filter((p) =>
          moment(p.startDate).isAfter(moment().endOf('day'))
        ) || []
      // Save back the filtered specials that are all in the future back to local storage
      LocalStorageHelper.setItem(
        this.localStorageKey,
        futureLocalStorageSpecials
      )

      // Set the rows up-front so the page never renders blank - the regionalPriceMap
      // enrichment below is a nice-to-have refinement of these same rows, not a
      // prerequisite for them appearing at all.
      this.specials = futureLocalStorageSpecials.length
        ? futureLocalStorageSpecials
        : [{ ...initialSpecial }]

      this.specials.forEach((special, index) => {
        if (special?.selectedProduct?.productCode) {
          this.cacheProducts([special.selectedProduct])
          this.pinSelectedProductOption(index, special.selectedProduct)
        }
      })

      try {
        await this.fetchFamilyGroups(this.selectedSupplierId)
        this.specials = this.specials.map((special) =>
          this.rehydrateSpecial(special)
        )
      } catch (e) {
        this.addNotification({
          color: 'error',
          text: 'Product data could not be loaded, pricing may be incomplete...'
        })
      }
    } catch (e) {
      this.addNotification({
        color: 'error',
        text: 'Specials data could not be loaded, please try again...'
      })
    }
  }
}
</script>

<style lang="scss" scoped>
#multi-special-upload {
  .upload-specials-col {
    position: sticky;
    bottom: 0;
    margin: 0;
    padding-bottom: 0;
    width: 100%;
    .upload-specials-btn {
      width: 220px;
    }
  }
}
</style>
